import {
  Account,
  AuthConfig,
  groupQueue,
  Modes,
  Queue,
  queries,
  RunCode,
  Test,
  Threat,
} from "@theprelude/core";
import { v5 as uuid } from "uuid";
import { createQuery } from "@/lib/create-query";
import { getFrequency } from "@/lib/frequency";

export interface TestRow {
  account_id: string | undefined;
  author: string | undefined;
  rowId: string;
  id: string;
  testId: string;
  name: string;
  technique?: string;
  frequency?: string;
  runCode?: RunCode;
  target?: string[] | null;
  isScheduled: boolean;
}

export const getTestsData = (tests: Test[], queue: Queue[]) => {
  const testQueue = groupQueue(
    queue.filter((q) => q.test !== null),
    "test",
  );

  const scheduled = testQueue.map((queue) => {
    const test = tests.find((t) => t.id === queue.id);
    if (!test) {
      return {
        rowId: queue.id,
        id: queue.id,
        testId: queue.id,
        name: queue.id,
        account_id: undefined,
        author: undefined,
        technique: undefined,
        frequency: getFrequency(queue.run_code),
        runCode: queue.run_code,
        target: queue.tags,
        isScheduled: true,
      };
    }

    return {
      rowId: uuid(
        JSON.stringify([queue.id, queue.run_code, queue.tags]),
        queue.id,
      ),
      id: test.id,
      testId: test.id,
      account_id: test.account_id ?? undefined,
      name: test.name,
      author: test.author ?? undefined,
      technique: test.technique ?? undefined,
      frequency: getFrequency(queue.run_code),
      runCode: queue.run_code,
      target: queue.tags,
      isScheduled: true,
    };
  });

  const unscheduled = tests
    .filter((test) => !testQueue.some((q) => q.id === test.id))
    .filter((test) => !test.tombstoned)
    .map((test) => ({
      rowId: test.id,
      id: test.id,
      account_id: test.account_id ?? undefined,
      author: test.author ?? undefined,
      testId: test.id,
      technique: test.technique ?? undefined,
      name: test.name,
      isScheduled: false,
    }));

  const allRows: TestRow[] = [...scheduled, ...unscheduled];

  return {
    data: tests,
    rows: allRows,
    count: {
      all: allRows.length,
      scheduled: scheduled.length,
      unscheduled: unscheduled.length,
    },
  };
};

export interface ThreatRow {
  id: string;
  rowId: string;
  threatId: string;
  source_id?: string;
  source?: string;
  name: string;
  threat: string;
  frequency?: string;
  runCode?: RunCode;
  target?: string[] | null;
  isScheduled: boolean;
}

export const getThreatsData = (threats: Threat[], queue: Queue[]) => {
  const threatQueue = groupQueue(
    queue.filter((q) => q.threat !== null),
    "threat",
  );

  const scheduled = threatQueue.map((queue) => {
    const threat = threats.find((t) => t.id === queue.id);
    if (!threat) {
      return {
        rowId: queue.id,
        id: queue.id,
        threatId: queue.id,
        source_id: undefined,
        source: undefined,
        target: queue.tags,
        threat: queue.id,
        name: queue.id,
        frequency: getFrequency(queue.run_code),
        runCode: queue.run_code,
        isScheduled: true,
      };
    }

    return {
      rowId: uuid(
        JSON.stringify([queue.id, queue.run_code, queue.tags]),
        queue.id,
      ),
      id: threat.id,
      threatId: threat.id,
      threat: threat.name,
      name: threat.name,
      source_id: threat.source_id ?? undefined,
      source: threat.source ?? undefined,
      frequency: getFrequency(queue.run_code),
      runCode: queue.run_code,
      target: queue.tags,
      isScheduled: true,
    };
  });

  const unscheduled = threats
    .filter((threat) => !threatQueue.some((q) => q.id === threat.id))
    .filter((threat) => !threat.tombstoned)
    .map((threat) => ({
      id: threat.id,
      rowId: threat.id,
      threatId: threat.id,
      threat: threat.name,
      name: threat.name,
      source_id: threat.source_id ?? undefined,
      source: threat.source ?? undefined,
      isScheduled: false,
      tombstoned: threat.tombstoned,
    }));

  const allRows: ThreatRow[] = [...scheduled, ...unscheduled];
  return {
    data: threats,
    rows: allRows,
    count: {
      all: allRows.length,
      scheduled: scheduled.length,
      unscheduled: unscheduled.length,
    },
  };
};

function getScheduleData(account: Account, tests: Test[], threats: Threat[]) {
  return {
    autopilotEnabled: account.mode === Modes.AUTOPILOT,
    allTestsPaused: account.mode === Modes.FROZEN,
    tests: getTestsData(tests, account.queue),
    threats: getThreatsData(threats, account.queue),
  };
}

export const scheduleQueryOptions = (authConfig: AuthConfig) =>
  createQuery({
    queries: [
      queries.iam.getAccount(authConfig),
      queries.detect.tests(authConfig),
      queries.detect.threats(authConfig),
    ],

    queryFn: getScheduleData,
  });
